import {
  Avatar,
  Badge,
  Box,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import type { FC } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { BatchReceiptDetails } from './ServiceProviderReceiptBatchForm';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { Iconify, Label, OneLineTypography } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { experimental_sx as sx } from '@mui/system';
import { motion } from 'framer-motion';
import NoReceiptImagesSvg from '../assets/undraw_no_receipt_batch_images.svg';
import { isNotEmpty } from '@pflegenavi/shared/utils';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';

const ActiveIndicator = styled(motion.div)(
  sx({
    backgroundColor: 'success.main',
    width: 5,
    right: 0,
    top: 0,
    bottom: 0,
    position: 'absolute',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  })
);

interface ReceiptBatchDetailsReceiptsProps {
  receipts: BatchReceiptDetails[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  tooltipForInactiveResidentMessage?: string;
  autoGenerateReceipts: boolean;
}

export const ServiceProviderReceiptBatchDetailsReceipts: FC<
  ReceiptBatchDetailsReceiptsProps
> = ({
  receipts,
  activeIndex,
  setActiveIndex,
  tooltipForInactiveResidentMessage,
  autoGenerateReceipts,
}) => {
  useNavigateReceipts({
    activeIndex,
    setActiveIndex,
    receiptsLength: receipts.length,
  });
  return (
    <ServiceProviderReceiptBatchDetailsReceiptsView
      receipts={receipts}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      tooltipForInactiveResidentMessage={tooltipForInactiveResidentMessage}
      autoGenerateReceipts={autoGenerateReceipts}
    />
  );
};

export interface ReceiptBatchDetailsReceiptsViewProps
  extends ReceiptBatchDetailsReceiptsProps {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  tooltipForInactiveResidentMessage?: string;
  autoGenerateReceipts: boolean;
}

export const ServiceProviderReceiptBatchDetailsReceiptsView: FC<
  ReceiptBatchDetailsReceiptsViewProps
> = ({
  receipts,
  activeIndex,
  setActiveIndex,
  tooltipForInactiveResidentMessage,
  autoGenerateReceipts,
}) => {
  return (
    <ReceiptsViewContainer gap={2}>
      {receipts.map((receipt, index) => (
        <ServiceProviderReceiptBatchDetailsSingleReceipt
          key={receipt.id}
          index={index}
          residentName={receipt.residentName ?? '--'}
          individualDate={receipt.individualDate}
          lineItems={receipt.lineItems}
          receiptImageIds={receipt.receiptImageIds}
          onClick={() => setActiveIndex(index)}
          active={index === activeIndex}
          residentActive={receipt.residentActive ?? true}
          tooltipForInactiveResidentMessage={tooltipForInactiveResidentMessage}
          autoGenerateReceipts={autoGenerateReceipts}
        />
      ))}
    </ReceiptsViewContainer>
  );
};

interface UseNavigateReceiptsProps {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  receiptsLength: number;
}

const useNavigateReceipts = ({
  activeIndex,
  setActiveIndex,
  receiptsLength,
}: UseNavigateReceiptsProps) => {
  const [keyPressed, setKeyPressed] = useState(false);

  const handleKeyDown = useCallback(
    // eslint-disable-next-line complexity
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          if (!keyPressed && activeIndex > 0) {
            setKeyPressed(true);
            setTimeout(() => {
              setKeyPressed(false);
            }, 500);
            setActiveIndex(activeIndex - 1);
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!keyPressed && activeIndex < receiptsLength - 1) {
            setKeyPressed(true);
            setTimeout(() => {
              setKeyPressed(false);
            }, 500);
            setActiveIndex(activeIndex + 1);
          }
          break;
        default:
          break;
      }
    },
    [activeIndex, keyPressed, receiptsLength, setActiveIndex]
  );

  const handleKeyUp = useCallback(() => {
    setKeyPressed(false);
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [activeIndex, handleKeyDown, handleKeyUp, receiptsLength]);
};

export interface ServiceProviderReceiptBatchDetailsSingleReceiptProps {
  index: number;
  active: boolean;
  residentName: string;
  individualDate?: Date;
  lineItems: BatchReceiptDetails['lineItems'];
  receiptImageIds?: string[] | null;
  onClick?: () => void;
  residentActive: boolean;
  tooltipForInactiveResidentMessage?: string;
  autoGenerateReceipts: boolean;
}

const scrollIntoViewIfNecessary = (element: Element) => {
  const elementRect = element.getBoundingClientRect();

  const parentRect = element.parentElement?.getBoundingClientRect();

  if (!parentRect) {
    return;
  }

  if (elementRect.top <= parentRect.top) {
    element.scrollIntoView({ behavior: 'smooth' });
  } else if (elementRect.bottom >= parentRect.bottom) {
    element.scrollIntoView({
      block: 'end',
      behavior: 'smooth',
    });
  }
};

export const ServiceProviderReceiptBatchDetailsSingleReceipt: FC<
  ServiceProviderReceiptBatchDetailsSingleReceiptProps
> = ({
  index,
  residentName,
  individualDate,
  lineItems,
  receiptImageIds,
  onClick,
  active,
  residentActive,
  tooltipForInactiveResidentMessage,
  autoGenerateReceipts,
}) => {
  const { t } = useTranslation();
  const singleReceiptRef = React.useRef<Element | null>(null);

  useEffect(() => {
    if (active && singleReceiptRef.current) {
      scrollIntoViewIfNecessary(singleReceiptRef.current);
    }
  }, [active]);

  const markAsInactiveResident =
    !residentActive && tooltipForInactiveResidentMessage;

  const amount = useMemo(
    () => lineItems.reduce((acc, curr) => acc + (curr.amount ?? 0), 0),
    [lineItems]
  );
  const defaultLineItemName = t('receipts.batch.form.no-line-item-name');
  const notes = useMemo(
    () =>
      lineItems
        .map((item) =>
          isNotEmpty(item.name) ? item.name : defaultLineItemName
        )
        .join(', '),
    [lineItems, defaultLineItemName]
  );

  const singleReceiptContainer = (
    <SingleReceiptContainer onClick={onClick} ref={singleReceiptRef}>
      <SingleReceiptContent
        index={index}
        residentName={residentName}
        amount={amount}
        individualDate={individualDate}
        receiptImageIds={receiptImageIds}
        notes={notes}
        residentActive={!markAsInactiveResident}
        autoGenerateReceipts={autoGenerateReceipts}
      />
      {active && <ActiveIndicator layoutId="active-card-indicator" />}
    </SingleReceiptContainer>
  );

  return markAsInactiveResident ? (
    <Tooltip
      title={tooltipForInactiveResidentMessage}
      arrow
      placement={'right'}
    >
      <div>{singleReceiptContainer}</div>
    </Tooltip>
  ) : (
    singleReceiptContainer
  );
};

interface SingleReceiptContentProps {
  index: number;
  residentName: string;
  residentActive: boolean;
  amount?: number;
  notes?: string;
  receiptImageIds?: string[] | null;
  autoGenerateReceipts: boolean;
  individualDate?: Date;
}

const SingleReceiptContent: FC<SingleReceiptContentProps> = memo(
  // eslint-disable-next-line complexity
  ({
    index,
    amount,
    residentName,
    notes,
    receiptImageIds,
    residentActive,
    autoGenerateReceipts,
    individualDate,
  }) => {
    const fDate = useFormatDate();
    const { fCurrencyCents } = useFormatting();
    const { t } = useTranslation();
    const theme = useTheme();

    return (
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                bgcolor: theme.palette.grey['300'],
              }}
            >
              <Typography
                fontSize={15}
                fontWeight={600}
                color="primary"
                sx={{ mb: '-2px' }}
              >
                #{index + 1}
              </Typography>
            </Avatar>
            <Stack alignItems="flex-start" gap={0.3}>
              <OneLineTypography
                variant="subtitle2"
                // put explicitly width here because the alignItems="flex-start" in the parent stack affects the width of the child
                sx={{
                  width: 200,
                  textDecoration: residentActive ? 'none' : 'line-through',
                  color: residentActive ? undefined : '#ff8888',
                }}
                fontWeight={700}
              >
                {residentName}
              </OneLineTypography>
              <Label color="success" variant="ghost">
                {fCurrencyCents(amount ?? 0)}
              </Label>
              {individualDate && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                  }}
                >
                  {fDate(new Date(individualDate), YEAR_MONTH_DAY_SHORT_FORMAT)}
                </Typography>
              )}
            </Stack>
          </Stack>

          {receiptImageIds && receiptImageIds.length > 0 ? (
            <div style={{ position: 'relative' }}>
              <Badge
                badgeContent={receiptImageIds.length}
                invisible={receiptImageIds.length < 2}
                color="info"
                sx={{
                  '& .MuiBadge-badge': { fontSize: 12 },
                  left: 30,
                  top: -28,
                }}
              />
              <Iconify
                icon="mdi:receipt-text"
                color={theme.palette.success.main}
                fontSize={35}
              />
            </div>
          ) : autoGenerateReceipts ? (
            <Tooltip
              title={t('receipts.batch.one-receipt-will-be-auto-generated')}
            >
              <div>
                <Iconify
                  icon="mdi:receipt-text"
                  color={theme.palette.info.main}
                  fontSize={35}
                />
              </div>
            </Tooltip>
          ) : (
            <Tooltip title={t('receipts.batch.form.receipt-image-unavailable')}>
              <div>
                <Iconify
                  icon="mdi:receipt-text"
                  color={theme.palette.grey['400']}
                  fontSize={35}
                />
              </div>
            </Tooltip>
          )}
        </Stack>
        {notes && <Notes notes={notes} />}
      </Stack>
    );
  }
);

interface NotesProps {
  notes: string | null;
}

const Notes: FC<NotesProps> = ({ notes }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={notes} arrow placement="bottom">
      <Stack
        sx={{
          maxWidth: '100%',
          px: 2,
          py: 1,
          backgroundColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="caption"
          fontSize={13.5}
          fontWeight={600}
          color="text.secondary"
        >
          {t('receipts.batch.note')}
        </Typography>
        <OneLineTypography variant="caption" fontSize={13.5} color="grey.800">
          {notes}
        </OneLineTypography>
      </Stack>
    </Tooltip>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const NoReceiptsAdded = () => {
  const { t } = useTranslation();
  return (
    <Stack
      alignItems="center"
      sx={{
        p: 3,
        flex: 1,
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          flex: 1,
        }}
        gap={2}
      >
        <img src={NoReceiptImagesSvg} width={200} />
        <Stack alignItems="center" gap={1}>
          <Typography
            textTransform="uppercase"
            textAlign="center"
            fontWeight={700}
          >
            {t('receipts.batch.no-receipt-available')}
          </Typography>
          <Typography textAlign="center">
            {t('receipts.batch.no-receipt-available-description')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ReceiptsViewContainer = styled(Stack)(
  sx({
    overflowY: 'auto',
    maxHeight: '70vh',
    width: 350,
    overflowX: 'hidden',
    direction: 'rtl',
    pl: 1,
    ml: -1,
    pb: 2,
    '&::-webkit-scrollbar': {
      width: '0.3em',
      color: 'red',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0,0,0,.1)',
    },
    position: 'relative',
    zIndex: 0,
  })
);

const SingleReceiptContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withNotes',
})(({ theme }) =>
  sx({
    position: 'relative',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    boxShadow: theme.customShadows.card,
    borderRadius: 2,
    p: 2.5,
    width: 340,
    maxHeight: 250,
    cursor: 'pointer',
    userSelect: 'none',
    direction: 'ltr',
    overflow: 'visible',
    zIndex: undefined,
  })
);
